import { useState, useRef, useMemo, useCallback } from 'react';
import { useUpdateEvent, useDeleteEvent } from '../../calendar/hooks/useCalendarData';
import { buildEventPayload } from '../../calendar/lib/calendarHelpers';
import { toUserZoned } from '../../../shared/lib/tzDate';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';

// Extraído de `ComponentTaskHoy.jsx` (no contemplado en las fases A-E originales del plan --
// encontrado al re-medir tras cerrar D1+E1) -- el overlay de evento de calendario (abierto desde
// `handleTaskClick`, que se queda en el container por tocar tanto tareas normales como eventos)
// + el autocomplete de búsqueda del roadmap (tareas) y de triggers + el motor de drag&drop/
// resize de una tarea sobre el roadmap. `setActiveCalendarEvent` se expone para que
// `handleTaskClick` (container) pueda abrir el overlay.
export function useTaskRoadmapIntegration({ tasks, activeTab, userTz, toast, translate, updateTaskMutation }) {
    const updateEventMutation = useUpdateEvent();
    const deleteEventMutation = useDeleteEvent();

    // Calendar Event overlay states
    const [activeCalendarEvent, setActiveCalendarEvent] = useState(null);

    const handleCloseCalendarEvent = () => setActiveCalendarEvent(null);
    const handleSaveCalendarEvent = async (eventData) => {
        try {
            // 02.5-calendar.md §0.2 — `eventData` es el objeto plano de `NewEvent.onSave`;
            // `useUpdateEvent()` espera `{id, payload}` en snake_case. Pasarlo directo (como
            // antes) hacía que `payload` llegara `undefined` — un PUT sin body que el backend
            // aceptaba en silencio sin persistir nada.
            await updateEventMutation.mutateAsync({ id: eventData.id, payload: buildEventPayload(eventData) });
            handleCloseCalendarEvent();
            toast.success(translate('events.saved') || 'Evento guardado');
        } catch (error) {
            toast.error(translate('events.saveFailed') || 'Error al guardar el evento');
        }
    };
    const handleDeleteCalendarEvent = async (eventId) => {
        try {
            await deleteEventMutation.mutateAsync(eventId);
            handleCloseCalendarEvent();
        } catch (error) {
            toast.error(translate('events.deleteFailed') || 'Error al borrar el evento');
        }
    };

    // Roadmap Search State
    const [roadmapSearchQuery, setRoadmapSearchQuery] = useState('');
    const [showRoadmapSearch, setShowRoadmapSearch] = useState(false);
    const [showRoadmapTriggers, setShowRoadmapTriggers] = useState(false);
    const [focusedRoadmapTaskIdx, setFocusedRoadmapTaskIdx] = useState(-1);

    // Trigger Search State
    const [triggerSearchQuery, setTriggerSearchQuery] = useState('');
    const [showTriggerSearch, setShowTriggerSearch] = useState(false);
    const [focusedTriggerIdx, setFocusedTriggerIdx] = useState(-1);
    const [availableTriggers, setAvailableTriggers] = useState([]);

    // Roadmap Autocomplete Logic
    const roadmapSearchTasks = useMemo(() => {
        if (!showRoadmapSearch || activeTab !== 'flow') return [];
        let filtered = tasks;
        if (roadmapSearchQuery.trim()) {
            const q = roadmapSearchQuery.toLowerCase();
            filtered = filtered.filter(t => t.title?.toLowerCase().includes(q) || t.stage?.toLowerCase().includes(q));
        }

        const hasDate = (t) => Boolean(t.date || t.start_time);

        filtered.sort((a, b) => {
            const aHas = hasDate(a);
            const bHas = hasDate(b);
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
            return 0;
        });

        return filtered.slice(0, 30);
    }, [tasks, roadmapSearchQuery, showRoadmapSearch, activeTab]);

    // slice 02.1 · paso a2b — `ComponentTaskFlow` (roadmap) es `forwardRef`; los comandos
    // imperativos van por `flowRef.current.focusTask/focusNode` y los eventos hijo→padre por
    // callbacks. Cero `window` CustomEvents `roadmap-*`.
    const flowRef = useRef(null);

    const handleRoadmapTaskSelect = (task) => {
        setShowRoadmapSearch(false);
        setRoadmapSearchQuery('');
        setFocusedRoadmapTaskIdx(-1);
        // Con fecha: paneá hacia la tarea. Sin fecha: asignale una en el viewport actual.
        flowRef.current?.focusTask(task, Boolean(task.date || task.start_time));
    };

    const triggerSearchTasks = useMemo(() => {
        if (!showTriggerSearch || activeTab !== 'flow') return [];
        if (triggerSearchQuery.trim()) {
            const q = triggerSearchQuery.toLowerCase();
            return availableTriggers.filter(t => t.label?.toLowerCase().includes(q));
        }
        return availableTriggers;
    }, [availableTriggers, triggerSearchQuery, showTriggerSearch, activeTab]);

    const handleTriggerSelect = (node) => {
        setShowTriggerSearch(false);
        setTriggerSearchQuery('');
        setFocusedTriggerIdx(-1);
        flowRef.current?.focusNode(node);
    };

    // Drag-drop / resize de una tarea en el roadmap (callbacks de `ComponentTaskFlow`, paso a2b
    // — antes `roadmap-update-task` / `roadmap-resize-task`). `useUpdateTask` lleva la escritura
    // optimista en la cache + rollback.
    const handleRoadmapTaskDrop = useCallback(async ({ task, newDate, newY, newEndDate }) => {
        // fecha calendario leída en `userTz`, no en UTC crudo (`.toISOString()` podía dar el día
        // siguiente/anterior según la hora y el timezone).
        const zonedNewDate = toUserZoned(newDate, userTz);
        const newDateStr = `${zonedNewDate.getFullYear()}-${String(zonedNewDate.getMonth() + 1).padStart(2, '0')}-${String(zonedNewDate.getDate()).padStart(2, '0')}`;
        const patch = { date: newDateStr, start_time: newDate.toISOString(), roadmap_y: newY };
        if (newEndDate) patch.end_time = newEndDate.toISOString();
        try {
            await updateTaskMutation.mutateAsync({ id: task.id, patch });
        } catch (err) {
            console.error('Failed to update task date from roadmap', err);
            toast.error(getErrorMessage(err));
        }
    }, [userTz, updateTaskMutation, toast]);

    const handleRoadmapTaskResize = useCallback(async ({ task, newEndDate }) => {
        try {
            await updateTaskMutation.mutateAsync({ id: task.id, patch: { end_time: newEndDate.toISOString() } });
        } catch (err) {
            console.error('Failed to update task end_time from roadmap', err);
            toast.error(getErrorMessage(err));
        }
    }, [updateTaskMutation, toast]);

    return {
        activeCalendarEvent, setActiveCalendarEvent,
        handleCloseCalendarEvent, handleSaveCalendarEvent, handleDeleteCalendarEvent,

        roadmapSearchQuery, setRoadmapSearchQuery,
        showRoadmapSearch, setShowRoadmapSearch,
        showRoadmapTriggers, setShowRoadmapTriggers,
        focusedRoadmapTaskIdx, setFocusedRoadmapTaskIdx,
        roadmapSearchTasks, flowRef, handleRoadmapTaskSelect,

        triggerSearchQuery, setTriggerSearchQuery,
        showTriggerSearch, setShowTriggerSearch,
        focusedTriggerIdx, setFocusedTriggerIdx,
        availableTriggers, setAvailableTriggers,
        triggerSearchTasks, handleTriggerSelect,

        handleRoadmapTaskDrop, handleRoadmapTaskResize,
    };
}
